import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X, Shield } from "lucide-react";

const STORAGE_KEY = "enthida_consent";

const applyConsent = (decision) => {
  // PostHog
  try {
    if (decision === "accepted" && window.posthog) {
      window.posthog.opt_in_capturing?.();
      window.posthog.startSessionRecording?.();
    } else if (decision === "declined" && window.posthog) {
      window.posthog.opt_out_capturing?.();
      window.posthog.stopSessionRecording?.();
    }
  } catch (e) {
    // PostHog may not be available – silent
  }

  // Google Analytics 4 (Consent Mode v2)
  try {
    if (typeof window.gtag === "function") {
      window.gtag("consent", "update", {
        analytics_storage: decision === "accepted" ? "granted" : "denied",
      });
    }
  } catch (e) {
    // gtag may not be available – silent
  }
};

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let saved = null;
    try {
      saved = localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      // localStorage blocked
    }
    if (!saved) {
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  const handleChoice = (decision) => {
    try {
      localStorage.setItem(STORAGE_KEY, decision);
    } catch (e) {
      // ignore
    }
    applyConsent(decision);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 md:left-6 md:right-auto md:bottom-6 md:max-w-md z-[100] animate-fadeIn"
      data-testid="cookie-banner"
    >
      <div className="bg-gradient-to-br from-[#121212] to-[#0e0e0e] border border-white/10 rounded-2xl shadow-2xl shadow-black/60 backdrop-blur-xl p-5 relative overflow-hidden">
        <div className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-[#00d4aa]/50 to-transparent" />

        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#7ed321]/15 to-[#00d4aa]/15 flex items-center justify-center shrink-0">
            <Cookie size={18} className="text-[#00d4aa]" />
          </div>
          <div className="flex-1 pr-6">
            <h3 className="text-white font-medium text-base mb-1">Cookies & Privatsphäre</h3>
            <p className="text-[#a1a1aa] text-sm leading-relaxed">
              Wir verwenden Cookies und Analyse-Tools (Google Analytics), um die Seite zu verbessern.
              Sie können selbst entscheiden – Ihre Wahl bleibt gespeichert.
            </p>
          </div>
          <button
            onClick={() => handleChoice("declined")}
            aria-label="Schließen"
            className="absolute top-3 right-3 w-7 h-7 rounded-lg hover:bg-white/5 flex items-center justify-center text-[#71717a] hover:text-white transition-colors"
            data-testid="cookie-banner-close"
          >
            <X size={15} />
          </button>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 mb-3">
          <button
            type="button"
            onClick={() => handleChoice("accepted")}
            className="flex-1 bg-gradient-to-r from-[#7ed321] to-[#00d4aa] text-black font-semibold px-4 py-2.5 rounded-xl text-sm hover:scale-[1.01] transition-all duration-200 shadow-[0_0_15px_rgba(0,212,170,0.2)]"
            data-testid="cookie-accept"
          >
            Alle akzeptieren
          </button>
          <button
            type="button"
            onClick={() => handleChoice("declined")}
            className="flex-1 bg-white/5 border border-white/10 text-white font-medium px-4 py-2.5 rounded-xl text-sm hover:border-[#00d4aa]/40 hover:bg-white/10 transition-all duration-200"
            data-testid="cookie-decline"
          >
            Nur essenzielle
          </button>
        </div>

        <div className="flex items-center justify-between text-xs text-[#71717a]">
          <div className="flex items-center gap-1.5">
            <Shield size={11} className="text-[#00d4aa]/70" />
            <span>Keine Weitergabe an Dritte</span>
          </div>
          <Link
            to="/cookies"
            className="text-[#a1a1aa] hover:text-[#00d4aa] transition-colors underline underline-offset-2"
            data-testid="cookie-details-link"
          >
            Details
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
